import assert from 'assert';

import {
  Agent,
  Staff,
  ParticipantsResponse,
  CustomerListResponse,
  CustomerDetail,
  CustomerCallsResponse,
  UnifiedDialogResponse,
  UnifiedDialogEntry,
  MyCallsResponse,
  CallDetailResponse,
  ActivitiesResponse,
  AgentStatusResponse,
  OrdersResponse,
  AIAgentMessage,
  AIAgentThread,
  AIAgentMessagesResponse,
  AIAgentThreadsResponse,
} from './types';
import { API_HOST, YELP_INTEGRATION_URL } from './envs';
import { settingsService } from './services';
import { makeRequest } from './utils/makeRequest';

export const getUserTwilioToken = async(identity: string): Promise<string> => {
  const data = await makeRequest<{token: string}>(`${settingsService.getBaseLambdaEndpoint()}/token`, {
    method: 'POST',
    body: { identity },
  });
  return data.token;
};

export const updateAgentPhoneAssignee = async({
  identity,
  phoneNumber,
  status,
}: {
  identity: string;
  phoneNumber: string;
  status?: string;
}) => {
  await makeRequest(`${settingsService.getBaseLambdaEndpoint()}/phone-assignee`, {
    method: 'POST',
    body: { identity, phone_number: phoneNumber, status },
  });
};

export const getAgentPhoneNumber = async(agentEmail: string): Promise<string> => {
  const data = await makeRequest<{items: Array<{phone_number: string}>}>(
    `${settingsService.getBaseLambdaEndpoint()}/phone-settings/?email=${agentEmail}`,
  );

  const items = data.items;
  assert(Array.isArray(items), 'Unrecognized data returned');
  assert(items.length > 0, 'No phone numbers found');
  assert(items.length === 1, 'Multiple phone numbers returned, please leave only one');

  return items[0].phone_number;
};

export const getAgents = async(excludeIdentity?: string): Promise<Agent[]> => {
  const data = await makeRequest<Agent[]>(`${settingsService.getBaseLambdaEndpoint()}/agents`);

  if (excludeIdentity) {
    return data.filter((item) => item.identity !== excludeIdentity);
  }
  return data;
};

// Staff API

type StaffListResponse = {
  count: number;
  next: string | null;
  previous: string | null;
  results: Staff[];
};

/**
 * Get paginated list of staff members
 */
export const getStaffList = async(params?: {
  page?: number;
  page_size?: number;
  search?: string;
}): Promise<StaffListResponse> => {
  const queryParams = new URLSearchParams();

  if (params) {
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        queryParams.append(key, String(value));
      }
    });
  }

  const url = `${API_HOST}/api/phoneapp/agents/${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;

  return await makeRequest<StaffListResponse>(url, {
    useAuthService: true,
  });
};

/**
 * Get a single staff member by ID
 */
export const getStaffById = async(id: number): Promise<Staff> => {
  const url = `${API_HOST}/api/phoneapp/agents/${id}/`;
  return await makeRequest<Staff>(url, {
    useAuthService: true,
  });
};

export const updateCallHoldStatus = async({ call_sid, hold }: {call_sid: string; hold: boolean}) => {
  return await makeRequest(`${settingsService.getBaseLambdaEndpoint()}/hold_call`, {
    method: 'POST',
    body: { call_sid, hold },
  });
};

export const sendAgentHeartbeat = async(identity: string): Promise<{status: string}> => {
  return await makeRequest(`${settingsService.getBaseLambdaEndpoint()}/heartbeat`, {
    method: 'POST',
    body: { identity },
  });
};

export const getAgentStatus = async(identity: string): Promise<AgentStatusResponse> => {
  return await makeRequest<AgentStatusResponse>(
    `${settingsService.getBaseLambdaEndpoint()}/agent-status?identity=${encodeURIComponent(identity)}`,
  );
};

export const hangupCall = async({ call_sid }: {call_sid: string}) => {
  return await makeRequest(`${settingsService.getBaseLambdaEndpoint()}/hangup-call`, {
    method: 'POST',
    body: { call_sid },
  });
};

export const fetchCustomerData = async(phoneNumber: string) => {
  const params = new URLSearchParams({
    phone_number: phoneNumber,
  });

  return await makeRequest(`${API_HOST}/api/phoneapp/customers/?${params.toString()}`, {
    useAuthService: true,
  });
};

export const warmTransferToAgent = async({ agentIdentity, callSid }: {agentIdentity: string; callSid: string}) => {
  return await makeRequest(`${settingsService.getBaseLambdaEndpoint()}/warm-transfer`, {
    method: 'POST',
    body: { agent_identity: agentIdentity, call_sid: callSid },
  });
};

export const getParticipants = async(callSid: string) => {
  return await makeRequest<ParticipantsResponse>(`${settingsService.getBaseLambdaEndpoint()}/participants?call_sid=${callSid}`);
};

// Customers Page API

/**
 * Get paginated list of customers for the Customers page
 */
export const getCustomersList = async(params?: {
  page?: number;
  page_size?: number;
  search?: string;
  name?: string;
  email?: string;
  phone_number?: string;
  my_customers?: boolean;
  is_active?: boolean;
  ordering?: string;
  staff_id?: number;
}): Promise<CustomerListResponse> => {
  const queryParams = new URLSearchParams();

  if (params) {
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        queryParams.append(key, String(value));
      }
    });
  }

  const url = `${API_HOST}/api/phoneapp/customers/v2/${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;

  return await makeRequest<CustomerListResponse>(url, {
    useAuthService: true,
  });
};

/**
 * Get detailed information about a specific customer
 */
export const getCustomerDetail = async(customerId: number): Promise<CustomerDetail> => {
  return await makeRequest<CustomerDetail>(`${API_HOST}/api/phoneapp/customers/v2/${customerId}/`, {
    useAuthService: true,
  });
};

/**
 * Get call history for a specific customer
 */
export const getCustomerCalls = async(
  customerId: number,
  params?: {
    page?: number;
    page_size?: number;
  },
): Promise<CustomerCallsResponse> => {
  const queryParams = new URLSearchParams();

  if (params) {
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        queryParams.append(key, String(value));
      }
    });
  }

  const url = `${API_HOST}/api/phoneapp/customers/v2/${customerId}/calls/${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;

  return await makeRequest<CustomerCallsResponse>(url, {
    useAuthService: true,
  });
};

/**
 * Get unified dialog (messages, calls, notes, etc.) for a specific customer
 */
export const getCustomerUnifiedDialog = async(
  customerId: number,
  params?: {
    page?: number;
    page_size?: number;
  },
): Promise<UnifiedDialogResponse> => {
  const queryParams = new URLSearchParams();

  if (params) {
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        queryParams.append(key, String(value));
      }
    });
  }

  const url = `${API_HOST}/api/phoneapp/customers/v2/${customerId}/unified-dialog/${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;

  return await makeRequest<UnifiedDialogResponse>(url, {
    useAuthService: true,
  });
};

/**
 * Mark unified dialog as read for a customer
 */
export const markUnifiedDialogAsRead = async(
  customerId: number,
): Promise<{last_read_at: string; unread_count: number}> => {
  const url = `${API_HOST}/api/phoneapp/customers/v2/${customerId}/unified-dialog/mark-read/`;

  return await makeRequest(url, {
    method: 'POST',
    useAuthService: true,
  });
};

/**
 * Create an internal note for a customer
 */
export const createInternalNote = async(
  customerId: number,
  params: {
    text?: string;
    related_sms?: number;
    conversation?: number;
    attachments?: string[];
    is_system_generated?: boolean;
    external_source?: string;
    external_key?: string;
    meta?: Record<string, unknown>;
  },
) => {
  const url = `${API_HOST}/api/phoneapp/customers/v2/${customerId}/internal-notes/`;

  return await makeRequest(url, {
    method: 'POST',
    body: params,
    useAuthService: true,
  });
};

/**
 * Get paginated list of my calls (calls involving the authenticated user)
 */
export const getMyCalls = async(params?: {
  page?: number;
  page_size?: number;
}): Promise<MyCallsResponse> => {
  const queryParams = new URLSearchParams();

  if (params) {
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        queryParams.append(key, String(value));
      }
    });
  }

  const url = `${API_HOST}/api/phoneapp/me/calls/${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;

  return await makeRequest<MyCallsResponse>(url, {
    useAuthService: true,
  });
};

/**
 * Get call detail by ID
 */
export const getCallById = async(callId: number): Promise<CallDetailResponse> => {
  const url = `${API_HOST}/api/phoneapp/call/${callId}/`;

  return await makeRequest<CallDetailResponse>(url, {
    useAuthService: true,
  });
};

/**
 * Get call detail by external key (Twilio SID)
 * Returns 404 if not found yet (call record not created)
 */
export const getCallByExternalKey = async(externalKey: string): Promise<CallDetailResponse> => {
  const url = `${API_HOST}/api/phoneapp/calls/${externalKey}/`;

  return await makeRequest<CallDetailResponse>(url, {
    useAuthService: true,
  });
};

/**
 * Get current user information from /api/v1/me/
 */
export const getCurrentUser = async(): Promise<{id: number; gid: string; email: string; full_name: string; preferred_language: string}> => {
  const url = `${API_HOST}/api/v1/me/`;

  return await makeRequest<{id: number; gid: string; email: string; full_name: string; preferred_language: string}>(url, {
    useAuthService: true,
  });
};

export const getActivities = async(params?: {
  page?: number;
  page_size?: number;
  order?: number;
  customer?: number;
  contact?: number;
  user?: number;
  staff_id?: number;
  done?: boolean;
}): Promise<ActivitiesResponse> => {
  const queryParams = new URLSearchParams();

  if (params) {
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        queryParams.append(key, String(value));
      }
    });
  }

  const url = `${API_HOST}/api/tasks/activities/${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;

  return await makeRequest<ActivitiesResponse>(url, {
    useAuthService: true,
  });
};

/**
 * Pull activities from the queue and assign them to the current user.
 * Returns the count of activities assigned.
 *
 * POST /api/tasks/activities/pull/
 * Request: {intended_group: "am_ops"}
 * Response: 200 OK with {count: number}
 */
export const pullActivity = async(intendedGroup: string = 'am_ops'): Promise<number> => {
  const response = await makeRequest<{count: number}>(`${API_HOST}/api/tasks/activities/pull/`, {
    method: 'POST',
    body: { intended_group: intendedGroup },
    useAuthService: true,
  });
  return response.count;
};

/**
 * Return an activity to the queue, unassigning it from the current user.
 *
 * POST /api/tasks/activities/{id}/return-to-queue/
 * Response: 200 OK with {count: number}
 * Validation: returns 400 if not owner or activity completed
 */
export const returnActivityToQueue = async(activityId: number): Promise<number> => {
  const response = await makeRequest<{count: number}>(`${API_HOST}/api/tasks/activities/${activityId}/return-to-queue/`, {
    method: 'POST',
    useAuthService: true,
  });
  return response.count;
};

/**
 * Send an SMS message to a customer
 */
export const createSMS = async(
  customerId: number,
  params: {
    to_phone_number: string;
    message: string;
    from_phone_number?: string;
    media_urls?: string[];
  },
): Promise<UnifiedDialogEntry> => {
  const url = `${API_HOST}/api/phoneapp/customers/v2/${customerId}/sms/`;

  return await makeRequest<UnifiedDialogEntry>(url, {
    method: 'POST',
    body: params,
    useAuthService: true,
  });
};

/**
 * Send a Yelp message via the yelp-integration Lambda
 */
export const sendYelpMessage = async(params: {
  yelp_lead_key: string;
  message: string;
  to_phone_number?: string;
  attachments?: Array<{url: string; filename: string}>;
}): Promise<{result: string}> => {
  const response = await fetch(`${YELP_INTEGRATION_URL}/send-yelp-message`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(params),
  });

  if (!response.ok) {
    throw new Error(`Failed to send Yelp message: ${response.status}`);
  }

  return response.json();
};

/**
 * Send an email to a customer
 */
export const createEmail = async(
  customerId: number,
  params: {
    to_email_address: string;
    to_name?: string;
    subject: string;
    text_body?: string;
    html_body?: string;
    from_email_address?: string;
    from_name?: string;
    cc_addresses?: Array<{email: string; name: string}>;
    bcc_addresses?: Array<{email: string; name: string}>;
    secondary_to_addresses?: Array<{email: string; name: string}>;
    media_urls?: string[];
  },
): Promise<UnifiedDialogEntry> => {
  const url = `${API_HOST}/api/phoneapp/customers/v2/${customerId}/unified-dialog/email/send/`;

  return await makeRequest<UnifiedDialogEntry>(url, {
    method: 'POST',
    body: params,
    useAuthService: true,
  });
};

// Orders API

/**
 * Get paginated list of orders for the Orders pipeline page
 */
export const getOrders = async(params?: {
  page?: number;
  page_size?: number;
  my_orders?: boolean;
  is_active?: boolean;
  deal_status?: string;
  product?: string;
  ordering?: string;
  staff_id?: number;
}): Promise<OrdersResponse> => {
  const queryParams = new URLSearchParams();

  if (params) {
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        queryParams.append(key, String(value));
      }
    });
  }

  const url = `${API_HOST}/api/phoneapp/orders/v2/${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;

  return await makeRequest<OrdersResponse>(url, {
    useAuthService: true,
  });
};

// AI Agent API

/**
 * Create a new AI agent thread with an initial message
 * POST /api/phoneapp/ai-threads/messages/
 */
export const createAIAgentThread = async(content: string): Promise<AIAgentMessage> => {
  const url = `${API_HOST}/api/phoneapp/ai-threads/messages/`;

  return await makeRequest<AIAgentMessage>(url, {
    method: 'POST',
    body: { role: 'user', content },
    useAuthService: true,
  });
};

/**
 * Get messages for a specific thread
 * GET /api/phoneapp/ai-threads/<id>/messages/
 */
export const getAIAgentMessages = async(
  threadId: number,
  params?: {
    page?: number;
    page_size?: number;
  },
): Promise<AIAgentMessagesResponse> => {
  const queryParams = new URLSearchParams();

  if (params) {
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        queryParams.append(key, String(value));
      }
    });
  }

  const url = `${API_HOST}/api/phoneapp/ai-threads/${threadId}/messages/${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;

  return await makeRequest<AIAgentMessagesResponse>(url, {
    useAuthService: true,
  });
};

/**
 * Send a message to an existing thread
 * POST /api/phoneapp/ai-threads/<id>/messages/
 */
export const sendAIAgentMessage = async(threadId: number, content: string): Promise<AIAgentMessage> => {
  const url = `${API_HOST}/api/phoneapp/ai-threads/${threadId}/messages/`;

  return await makeRequest<AIAgentMessage>(url, {
    method: 'POST',
    body: { role: 'user', content },
    useAuthService: true,
  });
};

/**
 * Get list of all threads for current user
 * GET /api/phoneapp/ai-threads/
 */
export const getAIAgentThreads = async(params?: {
  page?: number;
  page_size?: number;
}): Promise<AIAgentThreadsResponse> => {
  const queryParams = new URLSearchParams();
  const page = params?.page ?? 1;
  const pageSize = params?.page_size ?? 20;

  if (params) {
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        queryParams.append(key, String(value));
      }
    });
  }

  const url = `${API_HOST}/api/phoneapp/ai-threads/${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;

  const response = await makeRequest<{
    count: number;
    next: string | null;
    previous: string | null;
    results: AIAgentThread[];
  }>(url, {
    useAuthService: true,
  });

  // Calculate pagination info
  const numPages = Math.ceil(response.count / pageSize);
  const hasNext = response.next !== null;

  return {
    page,
    count: response.count,
    num_pages: numPages,
    next: hasNext ? page + 1 : null,
    results: response.results,
  };
};

/**
 * Delete an AI agent thread (soft delete)
 * DELETE /api/phoneapp/ai-threads/<id>/delete/
 */
export const deleteAIAgentThread = async(threadId: number): Promise<void> => {
  const url = `${API_HOST}/api/phoneapp/ai-threads/${threadId}/delete/`;

  await makeRequest(url, {
    method: 'DELETE',
    useAuthService: true,
  });
};

